import { logEvent } from "firebase/analytics";
import { analytics } from "../services/firebase";

/**
 * Get localized error message
 * This is a workaround for i18next type issues with TS 4.9
 */
export function getLocalizedError(key: string): string {
    // Access i18n dynamically to avoid type errors
    try {
        // Support both CJS and ESM mock shapes: module.default or module
        const mod = require("../i18n/config");
        const i18nInstance = mod && (mod.default || mod);
        if (i18nInstance && typeof i18nInstance.t === "function") {
            const translated = i18nInstance.t(key);
            if (typeof translated === "string") {
                return translated;
            }
        }
    } catch (e) {
        // Fallback to German if i18n not available
    }

    // Fallback messages in German (default language)
    const fallbackMessages: Record<string, string> = {
        "errors.anErrorOccurred": "Ein Fehler ist aufgetreten.",
        "errors.incorrectLogin": "Deine Anmeldedaten sind nicht korrekt.",
        "errors.tooManyAttempts": "Zu viele Anmeldeversuche. Bitte versuche es später erneut.",
        "errors.networkError": "Netzwerkfehler. Bitte überprüfe deine Internetverbindung.",
    };

    return fallbackMessages[key] || fallbackMessages["errors.anErrorOccurred"];
}

/**
 * Sanitizes Firebase errors and logs them to Google Analytics
 * Returns a user-friendly error message without exposing sensitive information
 *
 * @param error - The error object from Firebase or other sources
 * @param context - Context string describing where the error occurred (e.g., "login", "signout")
 * @returns A localized user-friendly error message
 */
export function handleFirebaseError(error: unknown, context: string): string {
    // Extract error details for logging
    let errorCode = "";
    let errorMessage = "";

    if (error && typeof error === "object") {
        // Firebase errors typically have code and message properties
        if ("code" in error) {
            errorCode = String(error.code);
        }
        if ("message" in error) {
            errorMessage = String(error.message);
        }
    }

    // If we couldn't extract proper error info, convert to string
    if (!errorCode && !errorMessage) {
        errorMessage = String(error);
    }

    // Log detailed error to Google Analytics (for debugging)
    logEvent(analytics, "exception", {
        description: `${context}: ${errorCode || "unknown"} - ${errorMessage}`,
        fatal: false,
        context: context,
        error_code: errorCode,
    });

    // Return localized generic user-friendly message (don't expose error details to user)
    return getLocalizedError("errors.anErrorOccurred") || "Ein Fehler ist aufgetreten.";
}

/**
 * Handles authentication-specific errors with appropriate user messages
 *
 * @param error - The error object from Firebase Auth
 * @param context - Context string describing the auth operation
 * @returns A localized user-friendly error message
 */
export function handleAuthError(error: unknown, context: string): string {
    let errorCode = "";

    if (error && typeof error === "object" && "code" in error) {
        errorCode = String(error.code);
    }

    // Log to analytics
    handleFirebaseError(error, context);

    // Return specific user-friendly messages for common auth errors
    // All other errors return generic message to avoid exposing sensitive info
    switch (errorCode) {
        case "auth/wrong-password":
        case "auth/user-not-found":
        case "auth/invalid-credential":
        case "auth/invalid-email":
            return getLocalizedError("errors.incorrectLogin");
        case "auth/too-many-requests":
            return getLocalizedError("errors.tooManyAttempts");
        case "auth/network-request-failed":
            return getLocalizedError("errors.networkError");
        default:
            // For unknown errors, return generic message
            return getLocalizedError("errors.anErrorOccurred");
    }
}
